import ServiceUtils from "./service-utils";
import EmailSettings from "./email-settings";
import { getEntries, getEntryParams, replaceAll, uniq } from "../utils";
import IEmailSignature from "../interface/email-signature";
import IEmailCustomTag from "../interface/email-custom-tag";
import ServiceUtil from "../enum/service-util";
import LogicalValue from "../enum/logical-value";
import GdprStatus from "../enum/gdpr-status";
import EmailEventCalendar from "../enum/email-event-calendar";

class EmailRenderUtilsSingleton {

    /* Public Methods */

    public btnRender(name: string, url: string, colorBtn: string, colorTxt: string): string {
        return this.getButtonHtml(name.trim(), url.trim(), colorBtn.trim(), colorTxt.trim());
    }

    public prerenderForm(source: string, form: string): string {
        const prm: (string | number)[] = getEntryParams(form);
        const url: string = `[${ServiceUtil.Form}](${prm[0]};;${prm[4]})`;
        const name: string = prm[1] as string;
        const colorBtn: string = prm[2] as string;
        const colorTxt: string = prm[3] as string;
        source = replaceAll(source, form, this.btnRender(name, url, colorBtn, colorTxt));
        return source;
    }

    public prerenderAccess(source: string, acc: string): string {
        const prm: (string | number)[] = getEntryParams(acc);
        const url: string = `[${ServiceUtil.Access}](${prm[0]}${prm[4] ? `;;${prm[4]}` : ''})`;
        const name: string = prm[1] as string;
        const colorBtn: string = prm[2] as string;
        const colorTxt: string = prm[3] as string;
        source = replaceAll(source, acc, this.btnRender(name, url, colorBtn, colorTxt));
        return source;
    }

    public prerenderPoll(source: string, poll: string): string {
        const prm: (string | number)[] = getEntryParams(poll);
        const urlYes: string = `[${ServiceUtil.Poll}](${prm[2]};;${LogicalValue.True})`;
        const urlNo: string = `[${ServiceUtil.Poll}](${prm[2]};;${LogicalValue.False})`;
        const txtYes: string = prm[0] as string;
        const txtNo: string = prm[1] as string;
        source = replaceAll(source, poll, this.getPollHtml(urlYes, urlNo, txtYes, txtNo));
        return source;
    }

    public prerenderGdpr(source: string, gdpr: string): string {
        const prm: (string | number)[] = getEntryParams(gdpr);
        // TODO - Check the settings below
        // const settings: IGdprSettings = EmailSettings.gdprTextGet();
        const urlYes: string = `[${ServiceUtil.Gdpr}](${GdprStatus.Allowed})`;
        const urlNo: string = `[${ServiceUtil.Gdpr}](${GdprStatus.Declined})`;
        const txtYes: string = prm[0] as string;
        const txtNo: string = prm[1] as string;
        source = replaceAll(source, gdpr, this.getPollHtml(urlYes, urlNo, txtYes, txtNo));
        return source;
    }

    public prerenderEvent(source: string, event: string): string {
        const prm: (string | number)[] = getEntryParams(event);
        const params: string = prm.join(';;');
        const google: string = `[${ServiceUtil.Event}](${EmailEventCalendar.Google};;${params})`;
        const ios: string = `[${ServiceUtil.Event}](${EmailEventCalendar.iOS};;${params})`;
        const outlook: string = `[${ServiceUtil.Event}](${EmailEventCalendar.Outlook};;${params})`;
        const office: string = `[${ServiceUtil.Event}](${EmailEventCalendar.Office};;${params})`;
        source = replaceAll(source, event, this.getEventHtml(google, ios, outlook, office));
        return source;
    }

    public renderButtons(source: string): string {
        return ServiceUtils.replaceEntries(source, ServiceUtil.Button, (params: string[]) => {
            const http: string = 'http';
            let url: string = `${params[1]}`.trim();
            if (url.length && url.slice(0, http.length) !== http && !url.includes(ServiceUtil.Sub)) {
                url = `${http}://${url}`;
            }
            return this.btnRender(params[0], url, params[2], params[3]);
        });
    }

    public async replaceTagsToValue(original: string): Promise<string> {
        let result: string = `${original}`.trim();
        result = await this.replaceEntriesAsync(result, ServiceUtil.TagStart, async (params: (string | number)[]) => {
            const tag: IEmailCustomTag | null = await EmailSettings.tagLoad(params[0] as number);
            if (tag) {
                return tag.start;
            }
        });
        result = await this.replaceEntriesAsync(result, ServiceUtil.TagEnd, async (params: (string | number)[]) => {
            const tag: IEmailCustomTag | null = await EmailSettings.tagLoad(params[0] as number);
            if (tag) {
                return tag.end;
            }
        });
        return result;
    }

    public async renderSignatures(source: string): Promise<string> {
        return await this.replaceEntriesAsync(source, ServiceUtil.Sign, async (params: (string | number)[]) => {
            const sign: IEmailSignature | null = await EmailSettings.signLoad(params[0] as number);
            if (sign) {
                return this.renderLinebreaks(sign.html || '');
            }
        });
    }

    public renderLinebreaks(original: string): string {
        return original.replace(/\r?\n/g, '<br/>');
    }

    public unrenderLinebreaks(original: string): string {
        return original.replace(/<br\s*[\/]?>/gi, '\n');
    }

    public encodeSubstitutions(source: string): string {
        return ServiceUtils.replaceEntries(source, ServiceUtil.Sub, (params: string[]) => {
            return `|${ServiceUtil.Sub}-${params.join('-')}|`;
        });
    }

    public decodeSubstitutions(source: string): string {
        const regex: RegExp = new RegExp(`\\|${ServiceUtil.Sub}(.*?)\\|`, 'g');
        let result: string = `${source}`;
        uniq(getEntries(result, regex)).forEach((entry: string) => {
            const params: string[] = replaceAll(entry, '|', '').split('-').slice(1);
            result = replaceAll(result, entry, `[${ServiceUtil.Sub}](${params.join(';;')})`);
        });
        return result;
    }

    /* Private Methods */
    
    public async replaceEntriesAsync(original: string, entryName: string, callback: Function): Promise<string> {
        let result: string = `${original}`.trim();
        const entries: string[] = ServiceUtils.getEntries(result, entryName);
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const params: (string | number)[] = getEntryParams(entry);
            const replace: string = await callback(params) || '';
            result = replaceAll(result, entry, replace);
        }
        return result;
    }

    private getPollHtml(urlYes: string, urlNo: string, txtYes: string, txtNo: string): string {
        const result: string = `
            <table cellspacing="0" cellpadding="0">
                <tr>
                    ${this.getBtnHtml(txtYes, urlYes, '#00d084', '#ffffff', 200)}
                    <td style="width: 20px;"></td>
                    ${this.getBtnHtml(txtNo, urlNo, '#d15353', '#ffffff', 200)}
                </tr>
            </table>
        `;
        return this.prepareHtml(result);
    }

    public getButtonHtml(name: string, url: string, colorBtn: string, colorTxt: string): string {
        const result: string = `
            <table cellspacing="0" cellpadding="0">
                <tr>
                    ${this.getBtnHtml(name, url, colorBtn, colorTxt, 300)}
                </tr>
            </table>
        `;
        return this.prepareHtml(result);
    }

    public getEventHtml(google: string, ios: string, outlook: string, office: string): string {
        const widthTxt: number = 70;
        const widthSep: number = 0;
        const result: string = `
            <table cellspacing="0" cellpadding="0">
                <tr>
                    <td
                        align="center"
                        width="${widthTxt}"
                        >
                        <a href="${google}">Google</a>
                    </td>
                    <td
                        align="center"
                        width="${widthSep}"
                        >
                        |
                    </td>
                    <td
                        align="center"
                        width="${widthTxt}"
                        >
                        <a href="${ios}">iOS</a>
                    </td>
                    <td
                        align="center"
                        width="${widthSep}"
                        >
                        |
                    </td>
                    <td
                        align="center"
                        width="${widthTxt}"
                        >
                        <a href="${outlook}">Outlook</a>
                    </td>
                    <td
                        align="center"
                        width="${widthSep}"
                        >
                        |
                    </td>
                    <td
                        align="center"
                        width="${widthTxt}"
                        >
                        <a href="${office}">Office 365</a>
                    </td>
                </tr>
            </table>
        `;
        return this.prepareHtml(result);
    }

    private getBtnHtml(name: string, url: string, colorBtn: string, colorTxt: string, width: number): string {
        return `
            <td
                align="center"
                width="${width}"
                height="36"
                bgcolor="${ colorBtn }"
                style="
                    -webkit-border-radius:3px;
                    -moz-border-radius:3px;
                    border-radius:3px;
                    -webkit-box-shadow:0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.2),0px 1px 5px 0px rgba(0,0,0,0.2);
                    -moz-box-shadow:0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.2),0px 1px 5px 0px rgba(0,0,0,0.2);
                    box-shadow:0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.2),0px 1px 5px 0px rgba(0,0,0,0.2);
                    "
                >
                <a
                    ${ url ? `href="${url}"` : '' }
                    style="
                        font-size:12px;
                        font-family:Helvetica,Arial,sans-serif;
                        text-decoration:none;
                        text-transform: uppercase;
                        -webkit-letter-spacing: 2px;
                        -moz-letter-spacing: 2px;
                        -ms-letter-spacing: 2px;
                        letter-spacing: 2px;
                        line-height:36px;
                        width:100%;
                        display:inline-block;">
                    <span style="color: ${ colorTxt }">${ name }</span>
                </a>
            </td>
        `;
    }

    private prepareHtml(original: string): string {
        original = original.replace(/\r?\n/g, '');
        while (original.indexOf('  ') > -1) {
            original = original.replace('  ', ' ');
        }
        return original;
    }

}

const EmailRenderUtils = new EmailRenderUtilsSingleton();

export default EmailRenderUtils;